import { Component, OnInit, inject, signal, OnDestroy, computed } from '@angular/core';
import { Subscription } from 'rxjs';
import { Ripple } from 'primeng/ripple';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { InventoryService } from '@servicios/inventory/inventory.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FieldConfig } from '@sharedComponents/table-dynamic/interfaces';
import { ButtonConfig } from '@sharedComponents/modal-template/modal-template.component';
import { getEquipmentFieldConfig, buildEquipmentFormPayload } from '../../utils/equipment-form.config';
import { DatePipe } from '@angular/common';
import { UserService } from '@servicios/user.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { ConfigService } from '@servicios/config.service';
import { OnboardingService } from '@servicios/onboarding.service';
import { firstValueFrom } from 'rxjs';
import { EquipmentEventPrintService } from '@servicios/equipments/equipment-event-print.service';

import { fonts } from '@assets/fonts/pdfFonts';
import pdfFonts from "@assets/fonts/vfs_fonts";
import pdfMake from 'pdfmake/build/pdfmake.min';

@Component({
  selector: 'app-equipment-detail-page',
  templateUrl: './equipment-detail-page.component.html',
  styleUrls: ['./equipment-detail-page.component.css'],
  standalone: false
})
export class EquipmentDetailPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private inventoryService = inject(InventoryService);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private userService = inject(UserService);
  private authService = inject(AuthenticationService);
  private configService = inject(ConfigService);
  private onboardingService = inject(OnboardingService);
  private printService = inject(EquipmentEventPrintService);
  private tourSubscription?: Subscription;

  equipmentId = signal<string | null>(null);
  equipment = signal<any>(null);
  loading = signal(true);

  // Tabs
  maintenances = signal<any[]>([]);
  calibrations = signal<any[]>([]);
  usages = signal<any[]>([]);
  activeUsage = computed(() => this.usages().find(u => !u.end_date));
  statusHistory = signal<any[]>([]); // Status History

  // Analytics & Predictive
  analyticsData = signal<any>(null);
  predictiveScore = signal<any>(null);
  gaugeChartData = signal<any>(null);
  gaugeChartOptions = signal<any>(null);

  // Visibility toggles for annulled records
  showAnnulledMaintenance = signal(false);
  showAnnulledCalibration = signal(false);
  showAnnulledUsage = signal(false);

  // Form Modes
  maintenanceMode = signal<'SCHEDULE' | 'COMPLETE'>('COMPLETE');
  calibrationMode = signal<'SCHEDULE' | 'COMPLETE'>('COMPLETE');

  filteredMaintenances = computed(() => {
    const list = this.maintenances();
    if (this.showAnnulledMaintenance()) return list;
    return list.filter(m => m.status !== 'NULLED');
  });

  filteredCalibrations = computed(() => {
    const list = this.calibrations();
    if (this.showAnnulledCalibration()) return list;
    return list.filter(c => c.status !== 'NULLED');
  });

  filteredUsages = computed(() => {
    const list = this.usages();
    if (this.showAnnulledUsage()) return list;
    return list.filter(u => !u.is_annulled);
  });

  // Computed Summary Data
  daysUntilMaintenance = computed(() => {
    const next = this.equipment()?.next_maintenance_date;
    if (!next) return null;
    const diffTime = new Date(next).getTime() - new Date().getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  daysUntilCalibration = computed(() => {
    const next = this.equipment()?.next_calibration_date;
    if (!next) return null;
    const diffTime = new Date(next).getTime() - new Date().getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  maintenanceStatus = computed(() => {
    const days = this.daysUntilMaintenance();
    if (days === null) return 'No programado';
    if (days < 0) return 'Vencido';
    if (days <= 7) return 'Crítico';
    return 'Al día';
  });

  calibrationStatus = computed(() => {
    const days = this.daysUntilCalibration();
    if (days === null) return 'No programado';
    if (days < 0) return 'Vencido';
    if (days <= 15) return 'Vence pronto';
    return 'Vigente';
  });

  usageStats = computed(() => {
    const logs = this.usages();
    const last30Days = logs.filter(l => {
      const d = new Date(l.start_date);
      const now = new Date();
      return (now.getTime() - d.getTime()) <= (30 * 24 * 60 * 60 * 1000);
    });
    return {
      totalLast30: last30Days.length,
      totalEver: logs.length
    };
  });

  // Modals & Forms

  // Unified Event Drawer State
  
  recentEvents = computed(() => {
    return [...this.maintenances(), ...this.calibrations()]
      .filter(e => e.status !== 'NULLED')
      .sort((a,b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime())
      .map(e => {
        const typeLabel = e.type === 'CALIBRATION' ? 'Calibración' : 
                          (e.type === 'PREVENTIVE' ? 'Preventivo' : 
                          (e.type === 'CORRECTIVE' ? 'Correctivo' : 'Evento'));
        const dateStr = e.event_date ? new Date(e.event_date).toLocaleDateString('es-AR') : 'Sin fecha';
        const desc = e.description ? (e.description.length > 30 ? e.description.substring(0, 30) + '...' : e.description) : 'Sin detalles';
        return {
          label: `${dateStr} - ${typeLabel} (${desc})`,
          value: e.id
        };
      });
  });
  showEventDrawer = false;

  currentEventType: 'MAINTENANCE' | 'CALIBRATION' = 'MAINTENANCE';
  eventEditData: any = null;
  eventInitialTabIndex = 0;
  eventMode: 'SCHEDULE' | 'COMPLETE' = 'COMPLETE';

  openEventDrawer(type: 'MAINTENANCE' | 'CALIBRATION', data: any = null, tabIndex?: number) {
    this.currentEventType = type;
    this.eventEditData = data;
    if (data) {
      if (type === 'MAINTENANCE') {
        this.eventMode = data.status === 'SCHEDULLED' ? 'SCHEDULE' : 'COMPLETE';
      } else {
        this.eventMode = (data.status === 'SCHEDULLED' || data.result_approved === null) ? 'SCHEDULE' : 'COMPLETE';
      }
    } else {
      this.eventMode = 'COMPLETE';
    }
    this.eventInitialTabIndex = tabIndex !== undefined ? tabIndex : 0;
    this.showEventDrawer = true;
  }

  onEventSaved() {
    this.loadEquipmentData();
  }

  showMaintenanceModal = signal(false);
  showCalibrationModal = signal(false);
  showUsageModal = signal(false);
  expandedRowsMaintenance = signal<{ [key: string]: boolean }>({});
  expandedRowsCalibration = signal<{ [key: string]: boolean }>({});

  // New signals for improvements
  maintenanceTabIndex = signal(0);
  maintenanceConsumableTabIndex = signal(0);
  calibrationTabIndex = signal(0);
  calibrationConsumableTabIndex = signal(0);
  usageTabIndex = signal(0);
  activeMainTabIndex = signal(0);
  isEditingMaintenance = signal(false);
  isEditingCalibration = signal(false);
  isEditingUsage = signal(false);
  currentMaintenanceId = signal<string | null>(null);
  currentCalibrationId = signal<string | null>(null);
  currentUsageId = signal<string | null>(null);
  isAnnulling = signal<string | null>(null);

  printEvent(eventType: 'MAINTENANCE' | 'CALIBRATION', event: any) {
    console.log('PRINT EVENT DATA:', event);
    const data = {
      appSettings: this.configService.appSettings,
      eventType,
      event,
      equipment: this.equipment()
    };
    this.printService.printEventSheet(data);
  }

  // Edit Equipment Modal
  showEditModal = signal(false);
  equipmentFormValue = signal<any | null>(null);

  // Status Modal
  showStatusModal = signal(false);
  statusForm: FormGroup;

  usageForm: FormGroup;

  // Master Data
  brands = signal<any[]>([]);
  vendors = signal<any[]>([]);
  locations = signal<any[]>([]);
  users = signal<any[]>([]);
  inventoryItems = signal<any[]>([]); // To select consumables
  linkedConsumables = signal<any[]>([]); // Items specifically linked to this equipment
  usagePurposes = signal<any[]>([]); // New master data for usage logs
  filteredUsagePurposes = signal<any[]>([]);
  costConcepts = signal<any[]>([]);

  searchUsagePurposes(event: any) {
    const query = event.query.toLowerCase();
    const all = this.usagePurposes();
    const filtered = all.filter(p => p.name.toLowerCase().includes(query));
    
    // Add the current query as a suggestion if it doesn't exist
    if (query && !filtered.find(f => f.name.toLowerCase() === query)) {
      filtered.unshift({ name: event.query, id: null });
    }
    
    this.filteredUsagePurposes.set(filtered);
  }

  // Track selected IDs to show details in the list
  internalConsumablesMap = new Map<string, any>();
  selectedInternalItem: any = null;
  selectedInternalItemCal: any = null;


  constructor() {
    this.statusForm = this.fb.group({
      status: ['ACTIVE', Validators.required],
      status_change_reason: ['', Validators.required]
    });

    this.usageForm = this.fb.group({
      start_date: [new Date(), Validators.required],
      end_date: [null],
      user_id: ['', Validators.required],
      purpose: ['', Validators.required],
      notes: [''],
      id: [null]
    });
  }

  ngOnInit() {
    console.log('--- EquipmentDetailPageComponent caricado (v1.2) ---');
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.equipmentId.set(id);
        this.loadEquipmentData();
        this.loadMasterData();
      }
    });

    // Handle external service toggles
    
    
    this.tourSubscription = this.onboardingService.tourActions$.subscribe(_action => {
      // (reservado para futuras acciones del tour de detalle)
    });

    // Iniciar tour de detalle la primera vez
    this.startDetailTour();
  }

  ngOnDestroy() {
    this.tourSubscription?.unsubscribe();
  }

  startDetailTour(force = false) {
    this.onboardingService.startTour({
      id: 'equipment-detail-basics',
      steps: [
        {
          selector: '#tour-eq-detail-header',
          title: '📄 Ficha Técnica del Equipo',
          content: 'Acá encontrás toda la información del equipo: estado actual (con pill de color), código ISO, marca, número de serie y ubicación física en el laboratorio.',
          position: 'bottom'
        },
        {
          selector: '#tour-eq-detail-stats',
          title: '📅 Fechas Críticas',
          content: 'Esta barra muestra las fechas clave del equipo: próximo mantenimiento, próxima calibración y vencimiento de garantía. Las fechas en naranja/rojo requieren atención.',
          position: 'bottom'
        },
        {
          selector: '#tour-eq-detail-tabs',
          title: '🗂️ Registros del Equipo',
          content: 'Desde estas pestañas gestionás todo el ciclo de vida del equipo: Mantenimientos (preventivos y correctivos), Calibraciones con certificado, Bitácora de uso, Historial de estado y Documentación técnica.',
          position: 'top'
        },
        {
          selector: '#tour-eq-detail-report',
          title: '📊 Reporte Técnico PDF',
          content: 'Generá el reporte técnico completo del equipo en PDF: incluye información general, historial de mantenimientos, calibraciones y bitácora de uso. Ideal para auditorías técnicas.',
          position: 'left'
        }
      ]
    }, force);
  }

  async loadMasterData() {
    try {
      const [brandsRes, suppRes, locRes, itemsRes, usagePurpRes, costConceptsRes] = await Promise.all([
        this.inventoryService.getAll('brand', { page: 1, limit: 100 }),
        this.inventoryService.getAll('supplier', { page: 1, limit: 100 }),
        this.inventoryService.getAll('location', { page: 1, limit: 100 }),
        this.inventoryService.getAll('items', { page: 1, limit: 1000, itemType: 'CONSUMABLE' }),
        this.inventoryService.getAll('usage-purpose', { page: 1, limit: 100 }),
        this.inventoryService.getAll('cost-concepts', { page: 1, limit: 100 })
      ]);
      this.brands.set(brandsRes.data ?? []);
      this.vendors.set(suppRes.data ?? []);
      this.locations.set(locRes.data ?? []);
      this.usagePurposes.set(usagePurpRes.data ?? []);
      this.costConcepts.set(costConceptsRes?.data ?? []);
      this.inventoryItems.set((itemsRes.data ?? []).map((i: any) => ({
        label: `${i.name} (Stock: ${i.stock})`,
        value: i.id,
        stock: i.stock,
        name: i.name
      })));
      (itemsRes.data ?? []).forEach((i: any) => this.internalConsumablesMap.set(i.id, i));

      // Fetch users
      const usersRes = await firstValueFrom(this.userService.getUsersList()).catch(e => { console.error('Users error', e); return { data: { usersList: [] } }; });
      const formattedUsers = (usersRes?.data?.usersList || []).map((u: any) => ({
        ...u,
        fullName: `${u.lastName}, ${u.name}`
      }));
      this.users.set(formattedUsers);
    } catch (e) {
      console.error("Error loading master data", e);
    }
  }

  async loadEquipmentData() {
    this.loading.set(true);
    try {
      const eqId = this.equipmentId();
      if (!eqId) return;

      const details = await this.inventoryService.getOne('equipments' as any, eqId);
      if (details) {
        if (details.purchase_date) details.purchase_date = new Date(details.purchase_date);
        if (details.warranty_expiration) details.warranty_expiration = new Date(details.warranty_expiration);
        if (details.next_maintenance_date) details.next_maintenance_date = new Date(details.next_maintenance_date);
        if (details.next_calibration_date) details.next_calibration_date = new Date(details.next_calibration_date);
      }
      this.equipment.set(details);
      await this.loadRecords();
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el equipo' });
      this.goBack();
    } finally {
      this.loading.set(false);
    }
  }

  async loadRecords() {
    const eqId = this.equipmentId()!;
    try {
      const [mRes, cRes, uRes, sRes, analytics, predictive] = await Promise.all([
        this.inventoryService.getMaintenances(eqId),
        this.inventoryService.getCalibrations(eqId),
        this.inventoryService.getUsageLogs(eqId),
        this.inventoryService.getEquipmentStatusHistory(eqId), // Status History
        this.inventoryService.getEquipmentAnalytics(eqId).catch(() => null),
        this.inventoryService.getEquipmentPredictiveScore(eqId).catch(() => null)
      ]);
      this.maintenances.set((mRes || []).map((m: any) => ({
        ...m,
        maintenance_date: m.maintenance_date ? new Date(m.maintenance_date) : null,
        next_maintenance_date: m.next_maintenance_date ? new Date(m.next_maintenance_date) : null,
        event_date: m.event_date ? new Date(m.event_date) : (m.maintenance_date ? new Date(m.maintenance_date) : null)
      })));
      this.calibrations.set((cRes || []).map((c: any) => ({
        ...c,
        calibration_date: c.calibration_date ? new Date(c.calibration_date) : null,
        next_calibration_date: c.next_calibration_date ? new Date(c.next_calibration_date) : null,
        event_date: c.event_date ? new Date(c.event_date) : (c.calibration_date ? new Date(c.calibration_date) : null)
      })));
      this.usages.set((uRes || []).map((u: any) => ({
        ...u,
        start_date: u.start_date ? new Date(u.start_date) : null,
        end_date: u.end_date ? new Date(u.end_date) : null
      })));
      this.statusHistory.set((sRes || []).map((s: any) => ({
        ...s,
        changed_at: s.changed_at ? new Date(s.changed_at) : null
      })));

      if (analytics) this.analyticsData.set(analytics.data || analytics);
      if (predictive) {
        const pData = predictive.data || predictive;
        this.predictiveScore.set(pData);
        this.setupGaugeChart(pData.score || 0);
      }

      // Filter linked items and format them with stock
      const currentEq = this.equipment();
      if (currentEq && currentEq.linked_items) {
        this.linkedConsumables.set(currentEq.linked_items.map((i: any) => ({
          ...i,
          label: `${i.name} (Stock: ${i.stock})`,
          value: i.id
        })));
      }
    } catch (e) {
      console.error(e);
    }
  }

  getStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case 'OPERATIONAL':
      case 'ACTIVE': return 'success';
      case 'MAINTENANCE': return 'warn';
      case 'OUT_OF_SERVICE': return 'danger';
      case 'CALIBRATION': return 'info';
      default: return 'secondary';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'OPERATIONAL':
      case 'ACTIVE': return 'status-active';
      case 'MAINTENANCE': return 'status-maintenance';
      case 'OUT_OF_SERVICE': return 'status-out';
      case 'CALIBRATION': return 'status-maintenance';
      default: return '';
    }
  }

  translateStatus(status: any): string {
    const STATUS_MAP: Record<string, string> = {
      'ACTIVE': 'Activo / Operativo',
      'OPERATIONAL': 'Operativo',
      'OUT_OF_SERVICE': 'Fuera de Servicio',
      'MAINTENANCE': 'En Mantenimiento',
      'CALIBRATION': 'En Calibración',
      'WITHDRAWN': 'Retirado',
      'BROKEN': 'Dado de Baja'
    };
    return status ? (STATUS_MAP[status] || status) : 'Estado desconocido';
  }

  translateEventType(type: any): string {
    const TYPE_MAP: Record<string, string> = {
      'CALIBRATION': 'Calibración',
      'PREVENTIVE': 'Preventivo',
      'CORRECTIVE': 'Correctivo',
      'VERIFICATION': 'Verificación',
      'OTHER': 'Otro'
    };
    return type ? (TYPE_MAP[type] || type) : 'Tipo desconocido';
  }

  setupGaugeChart(score: number) {
    const documentStyle = getComputedStyle(document.documentElement);
    let color = documentStyle.getPropertyValue('--green-500');
    if (score < 50) color = documentStyle.getPropertyValue('--red-500');
    else if (score < 80) color = documentStyle.getPropertyValue('--yellow-500');

    this.gaugeChartData.set({
      datasets: [
        {
          data: [score, 100 - score],
          backgroundColor: [color, documentStyle.getPropertyValue('--surface-200')],
          borderWidth: 0,
          circumference: 180,
          rotation: 270,
          cutout: '80%'
        }
      ]
    });
    this.gaugeChartOptions.set({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      }
    });
  }

  // --- Consumables Handlers ---

  openNewUsage() {
    this.isEditingUsage.set(false);
    this.currentUsageId.set(null);
    this.usageForm.reset({
      id: null,
      start_date: new Date(),
      user_id: '',
      purpose: '',
      notes: ''
    });
    this.usageTabIndex.set(0);
    this.showUsageModal.set(true);
  }

  editUsage(u: any, tabIndex: number = 0) {
    this.isEditingUsage.set(true);
    this.currentUsageId.set(u.id);
    this.usageForm.patchValue({
      id: u.id,
      start_date: u.start_date ? new Date(u.start_date) : new Date(),
      end_date: u.end_date ? new Date(u.end_date) : null,
      user_id: u.user_id,
      purpose: u.purpose,
      notes: u.notes
    });
    this.usageTabIndex.set(tabIndex);
    this.showUsageModal.set(true);
  }

  async saveUsage() {
    if (this.usageForm.invalid) {
      this.usageForm.markAllAsTouched();
      return;
    }
    try {
      this.loading.set(true);
      const formValue = this.usageForm.value;
      const payload: any = {
        equipmentId: this.equipmentId(),
        start_date: formValue.start_date,
        end_date: formValue.end_date || undefined,
        user_id: formValue.user_id,
        purpose: typeof formValue.purpose === 'string' ? formValue.purpose : (formValue.purpose?.name || ''),
        notes: formValue.notes || undefined
      };

      if (this.isEditingUsage()) {
        payload.id = this.currentUsageId();
        await this.inventoryService.updateUsageLog(this.currentUsageId()!, payload);
        this.messageService.add({ severity: 'success', summary: 'Actualizado', detail: 'Registro de uso actualizado' });
      } else {
        await this.inventoryService.createUsageLog(payload);
        this.messageService.add({ severity: 'success', summary: 'Cargado', detail: 'Uso registrado' });
      }
      this.showUsageModal.set(false);
      this.currentUsageId.set(null);
      this.usageForm.reset({ id: null, start_date: new Date() });
      this.loadRecords();
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el uso' });
    } finally {
      this.loading.set(false);
    }
  }

  async quickFinishUsage(usage: any) {
    try {
      this.loading.set(true);
      // Clean payload to avoid non-whitelisted fields error (400 Bad Request)
      const payload = { 
        id: usage.id,
        equipmentId: usage.equipmentId,
        start_date: usage.start_date,
        end_date: new Date(),
        user_id: usage.user_id,
        purpose: usage.purpose,
        notes: usage.notes
      };
      await this.inventoryService.updateUsageLog(usage.id, payload);
      this.messageService.add({ severity: 'success', summary: 'Uso Finalizado', detail: 'El registro de uso se ha cerrado correctamente.' });
      this.loadEquipmentData();
    } catch (error: any) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error?.message || 'No se pudo finalizar el uso.' });
    } finally {
      this.loading.set(false);
    }
  }

  removeMaintenance(m: any) {
    this.confirmDelete(m.id, 'maintenance');
  }

  removeCalibration(c: any) {
    this.confirmDelete(c.id, 'calibration');
  }

  removeUsage(u: any) {
    this.confirmDelete(u.id, 'usage');
  }

  confirmDelete(id: string, type: 'maintenance' | 'calibration' | 'usage') {
    const labels = {
      maintenance: 'mantenimiento',
      calibration: 'calibración',
      usage: 'registro de uso'
    };

    this.confirmationService.confirm({
      message: `¿Está seguro de que desea anular este ${labels[type]}? Esta acción no se puede deshacer.`,
      header: 'Confirmar Anulación',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteRecord(id, type)
    });
  }

  async deleteRecord(id: string, type: 'maintenance' | 'calibration' | 'usage') {
    try {
      this.isAnnulling.set(id);
      if (type === 'maintenance') await this.inventoryService.removeMaintenance(id);
      if (type === 'calibration') await this.inventoryService.removeCalibration(id);
      if (type === 'usage') await this.inventoryService.removeUsageLog(id);

      this.messageService.add({ severity: 'success', summary: 'Anulado', detail: 'El registro ha sido anulado correctamente' });
      this.loadEquipmentData();
    } catch (e: any) {
      console.error(e);
      const isValidation = e?.status === 409 || e?.status === 400 || e?.status === 422 || (e?.error?.message && e?.status < 500);
      const detail = e?.error?.message || e?.message || 'No se pudo anular el registro';
      this.messageService.add({
        severity: isValidation ? 'warn' : 'error',
        summary: isValidation ? 'Atención' : 'Error',
        detail,
        life: 6000
      });
    } finally {
      this.isAnnulling.set(null);
    }
  }


  downloadReport() {
    const eq = this.equipment();
    if (!eq) return;

    const data = {
      appSettings: this.configService.appSettings,
      equipment: eq,
      maintenances: this.showAnnulledMaintenance() ? this.maintenances() : this.filteredMaintenances(),
      calibrations: this.showAnnulledCalibration() ? this.calibrations() : this.filteredCalibrations(),
      usages: this.showAnnulledUsage() ? this.usages() : this.filteredUsages()
    };

    this.printService.printEquipmentReport(data);
  }

  openEdit() {
    const item = this.equipment();
    if (!item) return;
    this.equipmentFormValue.set(item);
    this.showEditModal.set(true);
  }

  async saveEquipment(data: any) {
    try {
      await this.inventoryService.update('equipments', data.id, data);
      this.messageService.add({ severity: 'success', summary: 'Actualizado', detail: 'Equipo actualizado correctamente' });
      this.showEditModal.set(false);
      this.loadEquipmentData();
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el equipo' });
    }
  }

  goBack() {
    this.router.navigate(['/equipments']);
  }

  openStatusModal() {
    const current = this.equipment();
    if (!current) return;
    this.statusForm.reset({
      status: current.status || 'ACTIVE',
      status_change_reason: ''
    });
    this.showStatusModal.set(true);
  }

  async saveStatus() {
    if (this.statusForm.invalid) {
      this.statusForm.markAllAsTouched();
      return;
    }
    const val = this.statusForm.value;
    try {
      await this.inventoryService.update('equipments', this.equipmentId()!, { ...val, id: this.equipmentId() });
      this.messageService.add({ severity: 'success', summary: 'Estado Actualizado', detail: 'El estado del equipo se ha actualizado correctamente' });
      this.showStatusModal.set(false);
      this.loadEquipmentData();
    } catch (e: any) {
      if (e.message && e.message.includes('change reason is required')) {
        this.messageService.add({ severity: 'error', summary: 'Error de Validación', detail: 'Debe proveer una razón para el cambio de estado' });
      } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el estado' });
      }
    }
  }

  isFutureDate(date: any): boolean {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate.getTime() >= today.getTime();
  }
}
